import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { CHECK_USERNAME } from '../../../graphql/member';

interface UsernameInputProps {
    className?: string;
    disabled?: boolean;
    check?: boolean;
    required: boolean;
    value: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    usernameTakenMessage?: string;
}

const useUsernameInputComponent = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    checkEnabled: boolean
) => {
    const [username, setUsername] = useState(value);
    const [usernameTaken, setUsernameTaken] = useState<null | boolean>(null);
    const [invalidEmail, setInvalidEmail] = useState(false);

    const [check, { loading, error, data, refetch }] = useLazyQuery(CHECK_USERNAME, {
        variables: { username },
        fetchPolicy: 'network-only'
    });

    const handleCheckUsername = () => {
        if (!loading && data) {
            setUsernameTaken(data?.checkUsername);
        }
    };

    const handleUpdateUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
        const uName = e.target.value;
        setUsername(uName);
        onChange(e);
    };

    useEffect(() => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = emailRegex.test(username);
        setInvalidEmail(!isValid);

        if (checkEnabled && isValid) {
            refetch({ username }).then((r: any) => {
                console.log('checking username', r);
                setUsernameTaken(r?.data.checkUsername);
            });
        } else {
            // reset to null if checks are disabled
            setUsernameTaken(null);
        }
    }, [username, checkEnabled, refetch]);

    return {
        models: { username, usernameTaken, invalidEmail },
        operations: { handleUpdateUsername, handleCheckUsername },
    };
};

const UsernameInput: React.FC<UsernameInputProps> = ({ className, disabled, required, check = true, value, onChange, usernameTakenMessage }) => {
    const { models, operations } = useUsernameInputComponent(value, onChange, check);

    useEffect(() => {
        operations.handleUpdateUsername({
            target: {
                value,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    }, [value]);

    return (
        <Form.Group className={className} data-testid="user-account">
            <Form.Label>Username</Form.Label>
            <Form.Control
                required={required}
                type="email"
                name="username"
                placeholder="please enter an email address for your username"
                value={models.username}
                onChange={operations.handleUpdateUsername}
                isInvalid={models.usernameTaken || models.invalidEmail}
                isValid={!models.usernameTaken && !models.invalidEmail && !!models.username}
                disabled={disabled}
            />
            {check && models.usernameTaken && (
                <Form.Control.Feedback type="invalid">
                    {usernameTakenMessage ? usernameTakenMessage : <>
                        Username taken. Sign in or change your password <a href="./">here</a>.
                    </>}
                </Form.Control.Feedback>
            )}


            {check && models.invalidEmail && (
                <Form.Control.Feedback type="invalid">
                    Username must be a valid email address.
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

export default UsernameInput;
